import { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db, functions } from "../../firebase";
import { THEMEPARKS_WIKI_SOURCE } from "../../config/themeparksWiki";
import type { EntityType } from "../../types";
import "./themeparksWiki.css";

interface WikiPark {
  id: string;
  name: string;
}
interface WikiDestination {
  id: string;
  name: string;
  parks: WikiPark[];
}
interface DestinationsResponse {
  destinations: WikiDestination[];
}
interface WikiChild {
  id: string;
  name: string;
  entityType: string;
  location?: { latitude: number; longitude: number } | null;
}
interface ChildrenResponse {
  parentId: string;
  parentName: string;
  children: WikiChild[];
}

interface WikiLinkControlProps {
  externalId: string;
  externalSource: string;
  /** Called when the admin picks or clears a ThemeParks.wiki link. */
  onChange: (externalSource: string, externalId: string) => void;
  entityType: EntityType;
  /** Set when editing an existing entity — enables bulk-create of children. */
  entityId?: string;
}

const fetchWikiData = httpsCallable(functions, "fetchthemeparkswikidata");

// ThemeParks.wiki entityType -> our EntityType (best effort; falls back to ATTRACTION).
const WIKI_TYPE_MAP: Record<string, EntityType> = {
  ATTRACTION: "ATTRACTION",
  SHOW: "EXPERIENCE",
  RESTAURANT: "RESTAURANT",
  PARK: "PARK",
  HOTEL: "HOTEL",
  DESTINATION: "DESTINATION",
};

const CHILD_BROWSE_TYPES: ReadonlySet<EntityType> = new Set<EntityType>([
  "ATTRACTION",
  "EXPERIENCE",
  "RESTAURANT",
  "HOTEL",
]);

export function WikiLinkControl({
  externalId,
  externalSource,
  onChange,
  entityType,
  entityId,
}: WikiLinkControlProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [destinations, setDestinations] = useState<WikiDestination[] | null>(null);
  const [filter, setFilter] = useState("");
  const [children, setChildren] = useState<{ parentName: string; items: WikiChild[] } | null>(null);
  const [childFilter, setChildFilter] = useState("");
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [creating, setCreating] = useState(false);
  const [createdCount, setCreatedCount] = useState<number | null>(null);

  const linked = externalSource === THEMEPARKS_WIKI_SOURCE && !!externalId;
  const canBrowseChildren = CHILD_BROWSE_TYPES.has(entityType);

  const loadDestinations = async () => {
    setOpen(true);
    setChildren(null);
    if (destinations) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWikiData({ mode: "destinations" });
      setDestinations((res.data as DestinationsResponse).destinations ?? []);
    } catch {
      setError("Couldn't load ThemeParks.wiki destinations.");
    }
    setLoading(false);
  };

  const loadChildren = async (parentExternalId: string) => {
    setLoading(true);
    setError(null);
    setChildren(null);
    setSelected({});
    setCreatedCount(null);
    try {
      const res = await fetchWikiData({ mode: "children", externalId: parentExternalId });
      const data = res.data as ChildrenResponse;
      setChildren({ parentName: data.parentName, items: data.children ?? [] });
    } catch {
      setError("Couldn't load entities from ThemeParks.wiki.");
    }
    setLoading(false);
  };

  const pick = (id: string) => onChange(THEMEPARKS_WIKI_SOURCE, id);
  const clearLink = () => {
    onChange("", "");
    setChildren(null);
  };

  const bulkCreate = async () => {
    if (!entityId || !children) return;
    const toCreate = children.items.filter((c) => selected[c.id]);
    if (toCreate.length === 0) return;
    setCreating(true);
    setError(null);
    try {
      for (const c of toCreate) {
        await addDoc(collection(db, "entities"), {
          displayName: c.name,
          type: WIKI_TYPE_MAP[c.entityType] ?? "ATTRACTION",
          imageUrl: null,
          parentId: entityId,
          externalId: c.id,
          externalSource: THEMEPARKS_WIKI_SOURCE,
          activeFrom: null,
          activeTo: null,
          customConfig: {},
          activityOptions: [],
          eventOptions: [],
          location:
            c.location &&
            typeof c.location.latitude === "number" &&
            typeof c.location.longitude === "number"
              ? { latitude: c.location.latitude, longitude: c.location.longitude }
              : null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      setCreatedCount(toCreate.length);
      setSelected({});
    } catch {
      setError("Failed to create one or more entities.");
    }
    setCreating(false);
  };

  const filteredDestinations = (destinations ?? []).filter((d) => {
    const q = filter.toLowerCase().trim();
    if (!q) return true;
    return d.name.toLowerCase().includes(q) || d.parks.some((p) => p.name.toLowerCase().includes(q));
  });
  const filteredChildren = (children?.items ?? []).filter((c) => {
    const q = childFilter.toLowerCase().trim();
    return !q || c.name.toLowerCase().includes(q);
  });

  return (
    <div className="form-field wiki-link-control">
      <span className="form-label">ThemeParks.wiki Link</span>

      <input
        type="text"
        className="form-input"
        placeholder="ThemeParks.wiki entity ID (or browse below)"
        value={externalId}
        onChange={(e) => onChange(e.target.value ? THEMEPARKS_WIKI_SOURCE : "", e.target.value)}
      />

      <div className={`wiki-link-status${linked ? "" : " wiki-link-status-empty"}`}>
        {linked ? (
          <>
            Linked to ThemeParks.wiki · <code>{externalId}</code>
          </>
        ) : (
          "Not linked to ThemeParks.wiki."
        )}
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-small btn-secondary" onClick={loadDestinations}>
          {open ? "Reload list…" : linked ? "Change link…" : "Browse ThemeParks.wiki…"}
        </button>
        {linked && (
          <button type="button" className="btn btn-small btn-secondary" onClick={clearLink}>
            Unlink
          </button>
        )}
        {open && (
          <button type="button" className="btn btn-small btn-secondary" onClick={() => setOpen(false)}>
            Close
          </button>
        )}
      </div>

      {error && <div className="wiki-link-error">{error}</div>}

      {open && (
        <>
          {loading && <p className="text-muted">Loading…</p>}

          {!loading && !children && destinations && (
            <>
              <input
                type="text"
                className="form-input"
                placeholder="Filter destinations / parks…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              <div className="wiki-link-results">
                {filteredDestinations.length === 0 && (
                  <p className="wiki-link-group-label">No matches</p>
                )}
                {filteredDestinations.map((d) => (
                  <div key={d.id}>
                    <p className="wiki-link-group-label">{d.name}</p>
                    <button
                      type="button"
                      className="wiki-link-result"
                      onClick={() => pick(d.id)}
                    >
                      Use destination: {d.name}
                      <span className="wiki-link-result-sub">destination · {d.id}</span>
                    </button>
                    {d.parks.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className="wiki-link-result"
                        onClick={() => pick(p.id)}
                      >
                        {p.name}
                        <span className="wiki-link-result-sub">park · {p.id}</span>
                      </button>
                    ))}
                    {canBrowseChildren &&
                      d.parks.map((p) => (
                        <button
                          key={`browse-${p.id}`}
                          type="button"
                          className="wiki-link-result"
                          onClick={() => loadChildren(p.id)}
                        >
                          ↳ Browse entities inside {p.name}…
                        </button>
                      ))}
                  </div>
                ))}
              </div>
            </>
          )}

          {!loading && children && (
            <>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-small btn-secondary"
                  onClick={() => setChildren(null)}
                >
                  ← Back to destinations
                </button>
              </div>
              <input
                type="text"
                className="form-input"
                placeholder={`Filter entities in ${children.parentName}…`}
                value={childFilter}
                onChange={(e) => setChildFilter(e.target.value)}
              />
              <div className="wiki-link-results">
                {filteredChildren.length === 0 && (
                  <p className="wiki-link-group-label">No matches</p>
                )}
                {filteredChildren.map((c) => (
                  <div key={c.id} className="wiki-bulk-row">
                    {entityId && (
                      <input
                        type="checkbox"
                        checked={!!selected[c.id]}
                        onChange={(e) =>
                          setSelected((s) => ({ ...s, [c.id]: e.target.checked }))
                        }
                        aria-label={`Select ${c.name}`}
                      />
                    )}
                    <button type="button" className="wiki-link-result" onClick={() => pick(c.id)}>
                      {c.name}
                      <span className="wiki-link-result-sub">
                        {c.entityType.toLowerCase()} · {c.id}
                      </span>
                    </button>
                  </div>
                ))}
              </div>
              {entityId ? (
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-small"
                    onClick={bulkCreate}
                    disabled={creating}
                  >
                    {creating ? "Creating…" : "Create selected as child entities"}
                  </button>
                  {createdCount != null && (
                    <span className="text-muted">Created {createdCount}.</span>
                  )}
                </div>
              ) : (
                <p className="text-muted">
                  Save this entity first to bulk-create children from the wiki tree.
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
